/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';
import RequireForAccess from 'react-access';
import Paper from '@material-ui/core/Paper';
import { RouterStore } from 'mobx-react-router';

import AccountStore from 'stores/AccountStore';
import ActionDrawerStore from 'stores/ActionDrawerStore';
import UserStore from 'stores/UserStore';
import NavigationStore from 'stores/NavigationStore';
import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';
import ActionsList from 'components/ActionsList/ActionsList';
import Loading from 'components/Loading/Loading';
import LocationDetails from 'components/Locations/LocationDetails/LocationDetails';
import PageTitle from 'components/PageTitleLegacy/PageTitle';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import CentralForm from 'components/CentralForm/CentralForm';
import {
  ChangeUserRole,
  ViewAsUser,
  LoginAsUser,
  DisableAccess,
  EnableAccess,
  RevokeAccess,
  StartAuditByUsers,
  AssignOrChangeUserLocation,
  DeleteUserLocation,
} from 'views/ActionDrawer';
import Activity from 'components/Activity/Activity';
import { actionsOnAccountUsers } from 'permissions/account';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { maskable } from 'utils/maskableText';
import { ENV } from 'config';

import AssignedToUser from '../AssignedToUser/AssignedToUser';
import CurrentUserRole from '../CurrentUserRole/CurrentUserRole';
import { actionsListSpecificUser } from '../actionsListData';

import styles from 'common-scss/DetailsPage.module.scss';
import { generateUrl, user } from 'api/routes';

type InjectedProps = {
  accountStore: AccountStore,
  actionDrawerStore: ActionDrawerStore,
  userStore: UserStore,
  navigationStore: NavigationStore,
  routing: RouterStore,
} & TranslatorProps &
  RouteComponentProps;

@translate()
@inject('navigationStore', 'accountStore', 'actionDrawerStore', 'userStore', 'routing')
@observer
export default class SpecificUserDetail extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  componentDidMount() {
    const { accountStore, params, navigationStore } = this.props;
    const accountId = params.id;
    const userId = params.userId;

    const specificUser = accountStore && accountStore.specificUser;

    accountStore.loadDetails(accountId);
    accountStore.loadSpecificUser(userId, accountId);

    navigationStore.pushPath(0, '/accounts');
    navigationStore.pushPath(1, `/accounts/${accountId}`, 0);
    navigationStore.pushPath(2, `/accounts/${accountId}`, 1);
  }

  componentDidUpdate(prevProps: *) {
    const { accountStore, params } = this.props;
    const accountId = params.id;
    const userId = params.userId;

    if (prevProps.actionDrawerStore.loadParent) {
      accountStore.loadDetails(accountId);
      accountStore.loadSpecificUser(userId, accountId);
      this.props.actionDrawerStore.loadParent = false;
    }
  }

  componentWillUnmount() {
    this.props.actionDrawerStore.clearActionDrawer();
  }

  render() {
    const { t, actionDrawerStore, accountStore, userStore } = this.props;

    const { userActivity } = userStore;
    const { details, specificUser } = accountStore;

    if (accountStore.error) {
      return (
        <CentralForm>
          <div>
            <h1>Error</h1>
            <p>{accountStore.errorMessage}</p>
            <Link to="/accounts">{t('general:actions.back_to_trading_account_plural')}</Link>
          </div>
        </CentralForm>
      );
    }

    if (accountStore.loadingUsers || !specificUser || !details) {
      return (
        <PageWrapper>
          <Loading />
        </PageWrapper>
      );
    }

    const title = `Trading Accounts | ${details.details.name} | Manage Users |
                        ${specificUser.first_name} ${specificUser.last_name}`;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <PageTitle title={title} />

          <div className={styles['grid-wrapper']}>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>
                <h3>{t('general:users.labels.details')}</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <b>{t('general:labels.first_name')}:</b>
                      </td>
                      <td>{specificUser.first_name}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>{t('general:labels.last_name')}:</b>
                      </td>
                      <td>{specificUser.last_name}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>{t('general:labels.email_address')}:</b>
                      </td>
                      <td>{maskable(specificUser.email, 'email', ENV === 'demo')}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>{t('general:labels.contact_number')}:</b>
                      </td>
                      <td>{specificUser.tel}</td>
                    </tr>
                  </tbody>
                </table>
                {
                  <LocationDetails
                    title={t('general:users.labels.location')}
                    details={specificUser.location}
                  />
                }
                <CurrentUserRole user={specificUser} />
                <AssignedToUser user={specificUser} accountId={accountStore.accountId} />
              </div>
              <div className={styles['actions-panel']}>
                <StatusIndicator
                  title={t('general:users.labels.status')}
                  status={specificUser.account_user_enabled ? 'active' : 'inactive'}
                />
                {typeof specificUser.role_id === 'number' && (
                  <RequireForAccess permissions={actionsOnAccountUsers[specificUser.role_id] || []}>
                    <ActionsList data={specificUser} actions={actionsListSpecificUser} />
                  </RequireForAccess>
                )}
              </div>
            </Paper>

            <Paper className={styles['activity-panel']}>
              {specificUser.id && (
                <Activity
                  title={true}
                  url={generateUrl(user.activity, [specificUser.id])}
                  refresh={actionDrawerStore.success}
                  allowFilter={true}
                />
              )}
            </Paper>
          </div>
        </PageWrapper>

        <ActionDrawerWrapper
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              changeUserRole: (
                <ChangeUserRole
                  formData={specificUser}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              viewAsUser: (
                <ViewAsUser
                  formData={specificUser}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              loginAsUser: (
                <LoginAsUser
                  formData={specificUser}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              disableUserAccess: (
                <DisableAccess
                  formData={specificUser}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              enableUserAccess: (
                <EnableAccess
                  formData={specificUser}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              revokeUserAccess: (
                <RevokeAccess
                  formData={specificUser}
                  currentAccountDetails={accountStore.details.details}
                />
              ),
              startAuditByUsers: (
                <StartAuditByUsers user={actionDrawerStore.currentActionDrawerData} type="users" />
              ),
              assignOrChangeUserLocation: (
                <AssignOrChangeUserLocation
                  user={specificUser}
                  account={accountStore.details.details}
                />
              ),
              removeUserLocation: (
                <DeleteUserLocation user={specificUser} account={accountStore.details.details} />
              ),
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
